import { useCallback, useEffect, useRef, useState } from "react";
import { LIST_FILTER_API_PARAMS } from "../constants/listFilters";

const SEARCH_DEBOUNCE_MS = 400;

const defaultGetItemId = (item) => item.id;

export function useCursorPaginatedList({
    fetchFn,
    normalizeResponse,
    applyClientFilters,
    mergeItems,
    getItemId = defaultGetItemId,
    filters: externalFilters,
}) {
    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [lastDocId, setLastDocId] = useState(null);
    const [internalLimit, setInternalLimit] = useState(10);
    const [internalSearch, setInternalSearch] = useState("");
    const [internalDebouncedSearch, setInternalDebouncedSearch] = useState("");
    const [internalStartDate, setInternalStartDate] = useState("");
    const [internalEndDate, setInternalEndDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const usesExternalFilters = Boolean(externalFilters);
    const limit = usesExternalFilters ? externalFilters.limit : internalLimit;
    const search = usesExternalFilters ? (externalFilters.search ?? "") : internalSearch;
    const debouncedSearch = usesExternalFilters
        ? (externalFilters.debouncedSearch ?? "")
        : internalDebouncedSearch;
    const startDate = usesExternalFilters ? externalFilters.startDate : internalStartDate;
    const endDate = usesExternalFilters ? externalFilters.endDate : internalEndDate;

    const loadMoreRef = useRef(null);
    const fetchingMoreRef = useRef(false);
    const lastFetchedCursorRef = useRef(null);
    const itemsRef = useRef([]);

    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    useEffect(() => {
        if (usesExternalFilters) return undefined;
        const timer = setTimeout(
            () => setInternalDebouncedSearch(internalSearch),
            SEARCH_DEBOUNCE_MS
        );
        return () => clearTimeout(timer);
    }, [internalSearch, usesExternalFilters]);

    const buildParams = useCallback(
        (cursorId = null) => ({
            limit,
            search: debouncedSearch.trim() || undefined,
            [LIST_FILTER_API_PARAMS.START_DATE]: startDate || undefined,
            [LIST_FILTER_API_PARAMS.END_DATE]: endDate || undefined,
            last_doc_id: cursorId || undefined,
        }),
        [limit, debouncedSearch, startDate, endDate]
    );

    const fetchItems = useCallback(
        async ({ append = false, cursorId = null } = {}) => {
            if (append) {
                if (fetchingMoreRef.current) return;
                if (cursorId && cursorId === lastFetchedCursorRef.current) return;
                fetchingMoreRef.current = true;
                setLoadingMore(true);
            } else {
                lastFetchedCursorRef.current = null;
                setLoading(true);
                setError(null);
            }

            try {
                const data = await fetchFn(buildParams(cursorId));
                const normalized = normalizeResponse(data);
                const nextItems =
                    normalized.items ??
                    normalized.campaigns ??
                    normalized.posts ??
                    [];

                if (normalized.hasCursorPagination) {
                    if (append) {
                        lastFetchedCursorRef.current = cursorId;
                        const merged = mergeItems(itemsRef.current, nextItems, getItemId);
                        const addedNothing = merged.length === itemsRef.current.length;
                        const noNewItems = nextItems.length === 0;
                        const sameCursor =
                            normalized.lastDocId && normalized.lastDocId === cursorId;

                        setItems(merged);
                        setHasMore(
                            addedNothing || noNewItems || sameCursor
                                ? false
                                : Boolean(normalized.hasMore)
                        );
                    } else {
                        setItems(nextItems);
                        setHasMore(Boolean(normalized.hasMore));
                    }

                    setLastDocId(normalized.lastDocId);
                    return;
                }

                const filtered = applyClientFilters
                    ? applyClientFilters(nextItems, {
                          search: debouncedSearch,
                          startDate,
                          endDate,
                      })
                    : nextItems;

                setItems(filtered);
                setHasMore(false);
                setLastDocId(null);
            } catch (err) {
                const detail = err?.response?.data?.detail;
                const message =
                    err?.response?.data?.message ||
                    (typeof detail === "string" ? detail : null) ||
                    (Array.isArray(detail) ? detail.map((d) => d?.msg ?? d).join(", ") : null) ||
                    err?.message ||
                    "Failed to load items. Please try again.";

                if (!append) {
                    setError(message);
                    setItems([]);
                    setHasMore(false);
                    setLastDocId(null);
                }
            } finally {
                if (append) {
                    fetchingMoreRef.current = false;
                    setLoadingMore(false);
                } else {
                    setLoading(false);
                }
            }
        },
        [
            fetchFn,
            buildParams,
            normalizeResponse,
            applyClientFilters,
            mergeItems,
            getItemId,
            debouncedSearch,
            startDate,
            endDate,
        ]
    );

    useEffect(() => {
        fetchItems({ append: false });
    }, [fetchItems]);

    useEffect(() => {
        const node = loadMoreRef.current;
        if (!node || !hasMore || loading || loadingMore) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0]?.isIntersecting &&
                    hasMore &&
                    lastDocId &&
                    !loading &&
                    !loadingMore &&
                    !fetchingMoreRef.current &&
                    lastDocId !== lastFetchedCursorRef.current
                ) {
                    fetchItems({ append: true, cursorId: lastDocId });
                }
            },
            { rootMargin: "240px" }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, lastDocId, loading, loadingMore, fetchItems]);

    const result = {
        items,
        hasMore,
        loading,
        loadingMore,
        error,
        loadMoreRef,
        limit,
        search,
        startDate,
        endDate,
        debouncedSearch,
    };

    if (!usesExternalFilters) {
        result.setLimit = setInternalLimit;
        result.setSearch = setInternalSearch;
        result.setStartDate = setInternalStartDate;
        result.setEndDate = setInternalEndDate;
    }

    return result;
}

export function mergeById(existing, incoming, getId = defaultGetItemId) {
    const seen = new Set(existing.map(getId));
    const next = incoming.filter((item) => {
        const id = getId(item);
        return id && !seen.has(id);
    });
    return [...existing, ...next];
}
